import React from 'react';
import { serialize } from 'object-to-formdata';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { errorToRender } from 'helpers/errors';
import { useHttp } from 'hooks/http.hook';
import Cargo from 'models/cargo';
import Loading from 'components/Loading';
import FreightsParameters from 'components/FreightComponents/FreightsParametres';
import FreightsRoutes from 'components/FreightComponents/FreightsRoutes';
import FreightsDate from 'components/FreightComponents/FreightsDate';
import CargoPayment from 'components/Forms/CargoPayment';
import CargoDocuments from 'components/Forms/CargoDocuments';
import FreightsTransport from 'components/FreightComponents/FreightsTransport';
import AddCompanyInfo from 'components/Forms/AddCompanyInfoWithEmployee';
import { freightsValidateSchema, auctionValidateSchema } from 'helpers/freightFormConfig';
import FormikDateInput from 'components/InputsField/formikInputs/FormikDateField';
import ShadowBlock from 'components/common/Layout/ShadowBlock';
import Button from 'components/common/Button';
import Title from 'components/common/Layout/Title';
import FormRow from 'components/common/Layout/FormRow';
import Error from 'components/Forms/Error';
import { ERROR_PLACE } from 'components/InputsField/InputError';
import { sendAim } from 'helpers/statisticAim';

import BackButton from 'components/common/BackButton';
import styles from './styles.module.scss';

export const FREIGHT_MODE = {
  auction: 'auction',
  freight: 'freight',
} as const;

export type FreightMode = (typeof FREIGHT_MODE)[keyof typeof FREIGHT_MODE];

export interface AddFreightProps {
  urlToCreate: string;
  urlToRedirect: string;
  mode: FreightMode;
}

interface FormErrors {
  [key: string]: string;
}

const AddFreight: React.FC<AddFreightProps> = ({ urlToCreate, urlToRedirect, mode }) => {
  const { request, loading, error } = useHttp();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isAuction = mode === FREIGHT_MODE.auction;

  const initialValues = Cargo.initialValues();
  if (isAuction) {
    initialValues.expires = '';
  }
  const validationSchema = isAuction ? auctionValidateSchema(t) : freightsValidateSchema(t);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    setSubmitting(false);
    const cargo = Cargo.formData(values, isAuction);
    const formData = serialize(cargo, { indices: true });

    values.document &&
    values.document.forEach((el) => {
      formData.append('document', el);
    });

    request(urlToCreate, 'post', formData)
      .then((res) => {
        navigate(urlToRedirect);
        sendAim('cargo_add');
      })
      .catch((e) => {
        const errors: FormErrors = {};
        e.response.data.errors.forEach((error: any) => {
          errors[error.path[0]] = t(error.type);
        });
        setErrors(errors);
      });
  };

  return (
    <>
      <BackButton />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, submitCount }) => (
          <Form>
            <ShadowBlock>
              <FreightsParameters />
              <FreightsRoutes />
              <FreightsTransport />
              <FreightsDate />
              <CargoPayment />
              <CargoDocuments />
              {mode === FREIGHT_MODE.auction && (
                <>
                  <Title withMargin>{t('auction.form.dayEndTitle')}</Title>

                  <FormRow>
                    <FormikDateInput
                      name="expires"
                      label={t('auction.form.dayEndLabel')}
                      isRequired={true}
                      minDate={new Date().setHours(24, 0, 0, 0)}
                      errorMode={ERROR_PLACE.topRight}
                    />
                    <></>
                  </FormRow>
                </>
              )}
            </ShadowBlock>
            <AddCompanyInfo />
            <div className={styles.form__footer}>
              {submitCount > 0 && Object.entries(errors).length > 0 ? (
                <Error>{t('errors.mainFormError')}</Error>
              ) : (
                error && errorToRender(error, t)
              )}
              {loading && (
                <div className={styles.loading}>
                  <Loading />
                </div>
              )}
              <div className={styles.button__continue}>
                <Button disabled={loading} type="submit">
                  {t('add')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddFreight;
